import ChatSidebar from "../ChatSidebar";
import ChatWindow from "../ChatWindow";
import { useChat } from "@/context/ChatProvider.tsx";
import LogoToolBar from "@/assets/svgComponets/LogoToolbar.tsx";
import { useState } from "react";
import clsx from "clsx";
import useUser from "@integration/user/hooks/useUser.ts";
import ToolBar from "@/components/application/Toolbar";
import LeftBar from "@/components/application/LeftBar";
import { Outlet } from "react-router-dom";
import useUserPublic from "@integration/user/hooks/useUserPublic.ts";

export default function Layout() {
  const { isVisible } = useChat();
  const { userAuth } = useUser();
  const userId = userAuth?.id;
  const { user } = useUserPublic(userId!);

  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarExpanded((prev) => !prev);
  };

  return (
    <div className="flex relative bg-gradient-to-b from-card-teal-20 to-black flex-col min-h-screen scrollbar-custom bg-bg-app">
      <div className="fixed z-40 w-full">
        <ToolBar
          isToggleSidebar={isSidebarExpanded}
          onToggleSidebar={toggleSidebar}
          user={user!}
        />
      </div>

      <div className="flex flex-row min-h-screen">
        <div className="hidden fixed md:flex z-35">
          <LeftBar isExpanded={isSidebarExpanded} />
        </div>

        <main
          className={clsx(
            "flex-grow mt-20 z-30 overflow-auto scrollbar-custom transition-all duration-300",
            isSidebarExpanded ? "md:ml-[11.93rem]" : "md:ml-[4.4rem]",
            isVisible ? "mr-92" : "ml-0"
          )}
        >
          <Outlet />
        </main>
        {isVisible && (
          <div className="h-full z-40 right-0 fixed mt-20">
            <ChatSidebar />
          </div>
        )}
      </div>

      <div className="fixed bottom-0 right-0 z-50">
        <ChatWindow />
      </div>

      <footer className={clsx(
        "border-t hidden sm:flex border-gray-800/50 text-white px-10 py-4 bg-gradient-to-b from-gray-800/60 to-gray-900/60 backdrop-blur-sm relative overflow-hidden mt-auto z-20 transition-all duration-300",
        isSidebarExpanded ? "md:ml-[11.93rem]" : "md:ml-[4.4rem]"
      )}>
        <div className="absolute inset-0 bg-gradient-to-r from-red-600/5 via-red-600/5 to-transparent opacity-50"></div>
        <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/5 rounded-full filter blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-red-500/5 rounded-full filter blur-3xl translate-y-1/2 -translate-x-1/2"></div>

        <div className="flex flex-wrap justify-around gap-4 px-4 md:px-6 relative z-10">
          <div className="flex flex-wrap max-w-xs p-3 bg-gradient-to-b from-gray-800/40 to-gray-900/40 rounded-lg border border-gray-700/50 shadow-lg">
            <LogoToolBar />
            <div className="h-0.5 w-24 bg-gradient-to-r from-red-500/30 to-transparent my-2"></div>
            <p className="text-gray-300 text-sm leading-relaxed">
              Somos apaixonados por e-sports, criando competições envolventes.
            </p>
          </div>

          <div className="flex flex-col gap-2 p-3">
            <h2 className="text-base font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent flex items-center gap-2">
              <div className="bg-gradient-to-br from-red-500/20 to-red-700/20 p-1 rounded-lg shadow-sm border border-red-500/10">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3 w-3 text-red-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z"
                  />
                </svg>
              </div>
              Acesso Rápido
            </h2>
            <div className="h-0.5 w-16 bg-gradient-to-r from-red-500/30 to-transparent mb-1"></div>
            <div className="space-y-1 text-sm">
              {["Início", "Campeonatos", "Configuração", "FAQ"].map(
                (item, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-1 text-gray-300 hover:text-white transition-colors cursor-pointer group"
                  >
                    <div className="w-1 h-1 rounded-full bg-red-500/50 group-hover:bg-red-500 transition-colors"></div>
                    <span>{item}</span>
                  </div>
                )
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2 p-3">
            <h2 className="text-base font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent flex items-center gap-2">
              <div className="bg-gradient-to-br from-red-500/20 to-red-700/20 p-1 rounded-lg shadow-sm border border-red-500/10">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3 w-3 text-red-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
              </div>
              Suporte
            </h2>
            <div className="h-0.5 w-16 bg-gradient-to-r from-red-500/30 to-transparent mb-1"></div>
            <div className="flex items-center gap-1 text-gray-300 hover:text-white transition-colors cursor-pointer group text-sm">
              <div className="w-1 h-1 rounded-full bg-red-500/50 group-hover:bg-red-500 transition-colors"></div>
              <span>Fale conosco</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
